import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["download", "progress", "results", "actions", "status", "delete", "error"]
    static values = {
        url: String,
        running: Boolean,
        interval: { type: Number, default: 1000 },
        errorMessage: String,
        stoppedMessage: String
    }

    connect() {
        this.connected = true
        this.failures = 0
        this.fragments = new Map()
        this.visibilityChanged = () => {
            clearTimeout(this.timer)
            if (!document.hidden && !this.request) this.schedule(0)
        }
        document.addEventListener("visibilitychange", this.visibilityChanged)
        this.schedule()
    }

    disconnect() {
        this.connected = false
        clearTimeout(this.timer)
        this.request?.abort()
        document.removeEventListener("visibilitychange", this.visibilityChanged)
    }

    schedule(delay = this.intervalValue) {
        clearTimeout(this.timer)
        if (!this.connected || !this.runningValue || document.hidden) return

        this.timer = setTimeout(() => this.refresh(), delay)
    }

    get pollingUrl() {
        return this.urlValue
    }

    async refresh() {
        if (!this.connected || !this.runningValue || this.request || document.hidden) return

        const request = new AbortController()
        this.request = request
        try {
            const response = await fetch(this.pollingUrl, {
                headers: { Accept: "application/json" },
                credentials: "same-origin",
                cache: "no-store",
                signal: request.signal
            })
            if (!this.connected || request.signal.aborted) return
            if ([401, 403, 404, 410].includes(response.status) || response.redirected) {
                this.runningValue = false
                this.showError(this.stoppedMessageValue)
                return
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const data = await response.json()
            if (!this.connected || request.signal.aborted) return
            this.applyProgress(data)
            this.failures = 0
            if (this.hasErrorTarget) this.errorTarget.classList.add("d-none")
        } catch (error) {
            if (!this.connected || request.signal.aborted) return
            this.failures += 1
            this.showError(this.errorMessageValue)
        } finally {
            if (this.request === request) this.request = null
            this.schedule(Math.min(this.intervalValue * (2 ** this.failures), 10000))
        }
    }

    applyProgress(data) {
        this.updateFragment("download", data.download_button)
        this.updateFragment("progress", data.export_progress)
        this.updateFragment("results", data.export_results)
        this.updateFragment("actions", data.export_actions)
        this.updateFragment("delete", data.delete_button)
        if (this.hasStatusTarget && this.statusTarget.textContent !== data.status) {
            this.statusTarget.textContent = data.status
        }
        this.runningValue = data.running === true
    }

    updateFragment(name, html) {
        const target = this.targets.find(name)
        if (!target || typeof html !== "string" || this.fragments.get(name) === html) return

        target.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(element => {
            window.bootstrap?.Tooltip.getInstance(element)?.dispose()
        })
        target.innerHTML = html
        this.fragments.set(name, html)
        target.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(element => {
            window.bootstrap?.Tooltip.getOrCreateInstance(element)
        })
    }

    showError(message) {
        if (!this.hasErrorTarget) return

        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("d-none")
    }
}
